import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TimePicker } from "@/components/ui/time-picker";
import { LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { PublicHolidaysCard } from "@/components/PublicHolidaysCard";
import { OfficeClosuresCard } from "@/components/OfficeClosuresCard";

export default function PreferencesPage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    working_hours_start: "09:00",
    working_hours_end: "17:00",
    lunch_start: "12:30",
    lunch_end: "13:30",
    default_view: "calendar",
    working_days: [1, 2, 3, 4, 5],
  });
  const [noLunch, setNoLunch] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      const hasLunch = !!(user.lunch_start && user.lunch_end && user.lunch_start !== user.lunch_end);
      setForm({
        working_hours_start: user.working_hours_start || "09:00",
        working_hours_end: user.working_hours_end || "17:00",
        lunch_start: hasLunch ? user.lunch_start : "12:30",
        lunch_end: hasLunch ? user.lunch_end : "13:30",
        default_view: user.default_view === "list" ? "list" : "calendar",
        working_days: Array.isArray(user.working_days) && user.working_days.length
          ? user.working_days
          : [1, 2, 3, 4, 5],
      });
      setNoLunch(!hasLunch);
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const lunchStart = noLunch ? null : (form.lunch_start || null);
      const lunchEnd = noLunch ? null : (form.lunch_end || null);
      await api.put("/users/me/preferences", {
        working_hours_start: form.working_hours_start || null,
        working_hours_end: form.working_hours_end || null,
        lunch_start: lunchStart,
        lunch_end: lunchEnd,
        default_view: form.default_view,
        working_days: form.working_days,
      });
      await refresh();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-8">
      <div className="max-w-3xl mx-auto w-full">
      <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Preferences</h1>
      <p className="text-sm text-slate-500 mt-1">Set your standard working hours. Time outside these hours will be lightly greyed on the calendar.</p>

      <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6 space-y-6">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-3" style={{ fontFamily: "Manrope" }}>Working hours</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Start</Label>
              <TimePicker value={form.working_hours_start}
                          onChange={(v) => setForm({ ...form, working_hours_start: v })}
                          className="mt-1 w-full" testId="prefs-work-start" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">End</Label>
              <TimePicker value={form.working_hours_end}
                          onChange={(v) => setForm({ ...form, working_hours_end: v })}
                          className="mt-1 w-full" testId="prefs-work-end" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700" style={{ fontFamily: "Manrope" }}>
              Lunch break <span className="text-xs font-normal text-slate-400">(optional)</span>
            </h2>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={noLunch}
                onChange={(e) => setNoLunch(e.target.checked)}
                data-testid="prefs-no-lunch-checkbox"
                className="h-4 w-4 accent-[#145c8c]"
              />
              No lunch break
            </label>
          </div>
          <div className={`grid grid-cols-2 gap-4 ${noLunch ? "opacity-40" : ""}`}>
            <div>
              <Label className="text-xs text-slate-500">From</Label>
              <TimePicker value={form.lunch_start}
                          onChange={(v) => setForm({ ...form, lunch_start: v })}
                          disabled={noLunch}
                          className="mt-1 w-full" testId="prefs-lunch-start" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">To</Label>
              <TimePicker value={form.lunch_end}
                          onChange={(v) => setForm({ ...form, lunch_end: v })}
                          disabled={noLunch}
                          className="mt-1 w-full" testId="prefs-lunch-end" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-1" style={{ fontFamily: "Manrope" }}>Working days</h2>
          <p className="text-xs text-slate-500 mb-3">Untick any day you don&apos;t normally work — it will show a shaded background on the calendar. You can still add entries on those days if you work occasionally.</p>
          <div className="flex flex-wrap gap-2" data-testid="prefs-working-days">
            {[
              { iso: 1, label: "Mon" },
              { iso: 2, label: "Tue" },
              { iso: 3, label: "Wed" },
              { iso: 4, label: "Thu" },
              { iso: 5, label: "Fri" },
              { iso: 6, label: "Sat" },
              { iso: 7, label: "Sun" },
            ].map((d) => {
              const active = form.working_days.includes(d.iso);
              return (
                <button
                  key={d.iso}
                  type="button"
                  onClick={() => setForm({
                    ...form,
                    working_days: active
                      ? form.working_days.filter((x) => x !== d.iso)
                      : [...form.working_days, d.iso].sort((a, b) => a - b),
                  })}
                  className={`h-10 min-w-[64px] px-3 rounded-md border text-sm font-medium transition-colors ${
                    active
                      ? "bg-[#145c8c] text-white border-[#145c8c]"
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`prefs-day-${d.label.toLowerCase()}`}
                  aria-pressed={active}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3" style={{ fontFamily: "Manrope" }}>Default Timer view</h2>
          <p className="text-xs text-slate-500 mb-3">Choose how the Timer page opens by default.</p>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden"
               data-testid="prefs-default-view-toggle">
            <button
              type="button"
              onClick={() => setForm({ ...form, default_view: "calendar" })}
              className={`h-9 px-4 text-sm flex items-center gap-2 transition-colors ${
                form.default_view === "calendar"
                  ? "bg-[#145c8c] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="prefs-default-view-calendar"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, default_view: "list" })}
              className={`h-9 px-4 text-sm flex items-center gap-2 border-l border-slate-200 transition-colors ${
                form.default_view === "list"
                  ? "bg-[#145c8c] text-white border-[#145c8c]"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="prefs-default-view-list"
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6 flex justify-end">
          <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#145c8c" }}
                  data-testid="prefs-save-button">
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </div>

      {user?.role === "admin" && <PublicHolidaysCard />}
      {user?.role === "admin" && <OfficeClosuresCard />}
      </div>
    </div>
  );
}
